import React from 'react';
import { Layers, FileText, Brain, Sparkles, Heart, Activity, BookOpen, Target, Zap } from 'lucide-react';
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
}
function FeatureCard({
  icon,
  title,
  description,
  onClick
}: FeatureCardProps) {
  return <div onClick={onClick} className={`
        bg-white rounded-xl p-5 border border-gray-100 shadow-sm
        ${onClick ? 'cursor-pointer hover:border-[#FECACA] hover:shadow-md transition-all duration-200' : ''}
      `} data-id="element-177">
      <div className="text-[#8B1538] mb-3" data-id="element-178">{icon}</div>
      <h3 className="font-semibold text-[#8B1538] mb-2" data-id="element-179">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-180">{description}</p>
    </div>;
}
interface OverviewProps {
  onNavigate?: (view: string) => void;
}
export function Overview({
  onNavigate
}: OverviewProps) {
  return <div className="animate-in fade-in duration-300" data-id="element-181">
      {/* Welcome Card */}
      <div className="bg-white rounded-xl p-6 md:p-8 mb-6 border border-gray-100 shadow-sm" data-id="element-182">
        <h2 className="text-xl md:text-2xl font-semibold text-[#8B1538] mb-4" data-id="element-183">
          Welcome to Jin's Three Needles Reference
        </h2>
        <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-184">
          <p data-id="element-185">
            Jin's Three Needles is a Lingnan-style acupuncture system developed
            by Professor Jin Rui at Guangzhou University of Chinese Medicine.
            The method uses small, highly focused three-point combinations to
            achieve powerful clinical effects across a wide range of conditions.
          </p>
          <p data-id="element-186">
            <strong className="text-[#8B1538]" data-id="element-187">Core Principle:</strong>{' '}
            Precision over quantity—each triad of points directs qi clearly to
            restore balance in both acute and chronic disorders.
          </p>
          <p data-id="element-188">
            This comprehensive reference guide provides instant access to all
            protocols, treatment techniques, point prescriptions, and clinical
            strategies developed by Dr. Jin Rui.
          </p>
        </div>
      </div>

      {/* Feature Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-189">
        <FeatureCard icon={<Layers className="h-8 w-8" data-id="element-191" />} title="Protocol Categories" description="Explore protocols organized by specialty: Neurology, Internal Medicine, Pediatrics, Musculoskeletal, and more." onClick={() => onNavigate?.('protocols')} data-id="element-190" />
        <FeatureCard icon={<FileText className="h-8 w-8" data-id="element-193" />} title="Point Prescriptions" description="Detailed point combinations with precise locations, needling depths, and clinical rationale for each protocol." onClick={() => onNavigate?.('protocols')} data-id="element-192" />
        <FeatureCard icon={<Brain className="h-8 w-8" data-id="element-195" />} title="TCM Patterns" description="Pattern differentiation guides for each condition, helping you select the most appropriate protocol." onClick={() => onNavigate?.('protocols')} data-id="element-194" />
        <FeatureCard icon={<Target className="h-8 w-8" data-id="element-197" />} title="Needling Technique" description="Dr. Jin's distinctive insertion, manipulation, and withdrawal methods for optimal therapeutic results." onClick={() => onNavigate?.('technique')} data-id="element-196" />
        <FeatureCard icon={<Activity className="h-8 w-8" data-id="element-199" />} title="Clinical Applications" description="Neurological conditions, pain management, pediatric disorders, and internal medicine with clear indications." onClick={() => onNavigate?.('protocols')} data-id="element-198" />
        <FeatureCard icon={<BookOpen className="h-8 w-8" data-id="element-201" />} title="About Dr. Jin Rui" description="Learn about Professor Jin Rui's legacy and the development of the Three Needles system." onClick={() => onNavigate?.('about')} data-id="element-200" />
      </div>

      {/* Why Three Needles Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-202">
        <div className="bg-[#8B1538] rounded-xl p-6 text-white" data-id="element-203">
          <div className="flex items-center gap-2 mb-3" data-id="element-204">
            <Sparkles className="h-6 w-6 text-[#FECACA]" data-id="element-205" />
            <h3 className="font-semibold text-lg" data-id="element-206">Why Three Needles?</h3>
          </div>
          <p className="text-[#FEE2E2] text-sm leading-relaxed" data-id="element-207">
            Dr. Jin believed that true acupuncture lies in{' '}
            <strong className="text-white" data-id="element-208">precision, not quantity</strong>.
            Decades of clinical research confirmed that three
            thoughtfully-chosen points can achieve powerful, lasting results
            across a wide range of conditions.
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-209">
          <div className="flex items-center gap-2 mb-3" data-id="element-210">
            <Zap className="h-6 w-6 text-[#8B1538]" data-id="element-211" />
            <h3 className="font-semibold text-lg text-[#8B1538]" data-id="element-212">
              Symbolic Meaning
            </h3>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed" data-id="element-213">
            In the <strong className="text-gray-800" data-id="element-214">Bagua system</strong>,
            three aligns with the eastern direction and wood element,
            representing growth and flowing qi. Each set of three needles
            embodies this continuous movement of life energy.
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-215">
        <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm" data-id="element-216">
          <div className="text-2xl font-bold text-[#8B1538]" data-id="element-217">45+</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-218">Protocols</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm" data-id="element-219">
          <div className="text-2xl font-bold text-[#8B1538]" data-id="element-220">15+</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-221">Categories</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm" data-id="element-222">
          <div className="text-2xl font-bold text-[#8B1538]" data-id="element-223">100+</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-224">Point Combinations</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm" data-id="element-225">
          <div className="text-2xl font-bold text-[#8B1538]" data-id="element-226">50+</div>
          <div className="text-xs text-gray-500 mt-1" data-id="element-227">Years of Research</div>
        </div>
      </div>
    </div>;
}
export function Technique() {
  return <div className="animate-in fade-in duration-300" data-id="element-228">
      {/* Header Card */}
      <div className="bg-white rounded-xl p-6 md:p-8 mb-6 border border-gray-100 shadow-sm text-center" data-id="element-229">
        <h2 className="text-xl md:text-2xl font-semibold text-[#8B1538] mb-4" data-id="element-230">
          Distinctive Needling Techniques
        </h2>
        <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-231">
          Dr. Jin Rui's acupuncture method is characterized by precision,
          intention, and a strong emphasis on practitioner–patient interaction.
          His approach reflects a commitment to traditional techniques that
          enhance therapeutic effectiveness.
        </p>
      </div>

      {/* Three Technique Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-232">
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-233">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-semibold mb-4 text-center" data-id="element-234">
            Insertion
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-235">
            Dr. Jin Rui practices a{' '}
            <strong className="text-gray-900" data-id="element-236">
              slow, deliberate insertion technique
            </strong>
            , directing his <em data-id="element-237">yi nian</em> (intention) precisely toward each
            point. This mindful approach focuses the flow of qi, enhances the{' '}
            <em data-id="element-238">de qi</em> sensation, and allows for exceptional accuracy in
            point location.
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-239">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-semibold mb-4 text-center" data-id="element-240">
            Manipulation
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-241">
            Dr. Jin exclusively uses{' '}
            <strong className="text-gray-900" data-id="element-242">
              manual needle manipulation, rotating the needles every 10 minutes,
              three to five times in each direction
            </strong>
            . He believes this hands-on approach deepens the therapeutic effect
            by keeping both practitioner and patient engaged.
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-243">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-semibold mb-4 text-center" data-id="element-244">
            Withdrawal
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-245">
            To minimize post-treatment swelling, Dr. Jin{' '}
            <strong className="text-gray-900" data-id="element-246">
              withdraws the needle at the same angle as the insertion angle and
              immediately applies firm pressure
            </strong>
            to the site after needle removal. This helps prevent bleeding and
            reduces localized swelling.
          </p>
        </div>
      </div>

      {/* Additional Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-247">
        <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-248">
          <h3 className="font-semibold text-[#8B1538] mb-3" data-id="element-249">
            Needle Insertion & Intention
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-250">
            Professor Jin applies a slow, deliberate insertion method, aligning
            intention closely with each point so qi is guided precisely. This
            requires the practitioner to focus their Shen (spirit) on the needle
            tip, deepening de qi and refining point location.
          </p>
        </div>
        <div className="bg-[#FEF2F2] rounded-xl p-6 border border-[#FECACA]" data-id="element-251">
          <h3 className="font-semibold text-[#8B1538] mb-3" data-id="element-252">⚠️ Safety Note</h3>
          <p className="text-sm text-[#991B1B] leading-relaxed" data-id="element-253">
            Withdraw along the original angle with firm post-removal pressure to
            reduce bleeding and swelling, especially for scalp points and points
            near vessels.
          </p>
        </div>
      </div>
    </div>;
}
export function AboutJin() {
  return <div className="animate-in fade-in duration-300" data-id="element-254">
      {/* Biography Card */}
      <div className="bg-white rounded-xl p-6 md:p-8 mb-6 border border-gray-100 shadow-sm" data-id="element-255">
        <h2 className="text-xl md:text-2xl font-semibold text-[#8B1538] mb-4" data-id="element-256">
          Professor Jin Rui
        </h2>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-257">
          <div className="flex-1 space-y-4 text-gray-700 leading-relaxed" data-id="element-258">
            <p data-id="element-259">
              Professor Jin Rui (1932–2010) was a distinguished professor at
              Guangzhou University of Chinese Medicine. He systematized Jin's
              Three Needles and developed many condition-specific triads over
              more than five decades of clinical work.
            </p>
            <p data-id="element-260">
              His work was instrumental in modernizing acupuncture education in
              southern China. He was known for his rigorous approach to point
              location and his deep understanding of classical texts, which he
              combined with modern anatomical knowledge.
            </p>
            <p data-id="element-261">
              The "Three Needles" system (San Zhen) was the culmination of his
              life's work, distilling complex treatments into elegant,
              reproducible, and highly effective protocols.
            </p>
          </div>
          <div className="md:w-1/3 flex-shrink-0" data-id="element-262">
            <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm" data-id="element-263">
              <img src="https://cdn.magicpatterns.com/uploads/8nqesL1jnDu7cXA5uG1z9c/image.png" alt="Professor Jin Rui" className="w-full h-auto object-cover" data-id="element-264" />
            </div>
            <p className="text-xs text-gray-500 text-center mt-2 italic" data-id="element-265">
              Professor Jin Rui (1932–2010)
            </p>
          </div>
        </div>
      </div>

      {/* Legacy Card */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-266">
        <h3 className="font-semibold text-[#8B1538] mb-4" data-id="element-267">
          Legacy & Contributions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-268">
          <div className="flex items-start gap-3" data-id="element-269">
            <div className="w-2 h-2 rounded-full bg-[#8B1538] mt-2 flex-shrink-0" data-id="element-270"></div>
            <p className="text-sm text-gray-700" data-id="element-271">
              Founded the Jin's Three Needles Research Center
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-272">
            <div className="w-2 h-2 rounded-full bg-[#8B1538] mt-2 flex-shrink-0" data-id="element-273"></div>
            <p className="text-sm text-gray-700" data-id="element-274">
              Authored multiple textbooks on Lingnan acupuncture
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-275">
            <div className="w-2 h-2 rounded-full bg-[#8B1538] mt-2 flex-shrink-0" data-id="element-276"></div>
            <p className="text-sm text-gray-700" data-id="element-277">
              Trained thousands of students who now practice globally
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-278">
            <div className="w-2 h-2 rounded-full bg-[#8B1538] mt-2 flex-shrink-0" data-id="element-279"></div>
            <p className="text-sm text-gray-700" data-id="element-280">
              Recognized as a Master of Traditional Chinese Medicine
            </p>
          </div>
        </div>
      </div>
    </div>;
}